import { useCallback, useState } from "react";

interface UseFlowStackResult<S extends string> {
  currentStep: S;
  push: (step: S) => void;
  pop: () => void;
  replace: (step: S) => void;
  reset: (step?: S) => void;
  canGoBack: boolean;
}

export function useFlowStack<S extends string>(initialStep: S): UseFlowStackResult<S> {
  const [stack, setStack] = useState<S[]>([initialStep]);

  const push = useCallback((step: S) => {
    setStack((previous) => [...previous, step]);
  }, []);

  const pop = useCallback(() => {
    setStack((previous) => (previous.length <= 1 ? previous : previous.slice(0, -1)));
  }, []);

  const replace = useCallback((step: S) => {
    setStack((previous) => [...previous.slice(0, -1), step]);
  }, []);

  const reset = useCallback(
    (step?: S) => {
      setStack([step ?? initialStep]);
    },
    [initialStep]
  );

  const currentStep = stack[stack.length - 1] as S;
  const canGoBack = stack.length > 1;

  return { currentStep, push, pop, replace, reset, canGoBack };
}
